import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMedia } from "react-use";

type ModalDesktopSize = "default" | "xl" | "screen";
type ModalDesktopHeight = "default" | "screen" | "90vh";
type ModalMobileHeight = "content" | "75vh" | "90vh";
type ModalPadding = "default" | "none";

type Props = {
  title?: string;
  description?: string;
  open: boolean;
  onOpenChange?: (value: boolean) => void;
  onAfterClose?: () => void;
  children: React.ReactNode;
  desktopSize?: ModalDesktopSize;
  desktopHeight?: ModalDesktopHeight;
  mobileHeight?: ModalMobileHeight;
  padding?: ModalPadding;
  /** Keep the modal open on outside clicks, for surfaces that hold unsaved input such as a review note. */
  dismissOnOutsideClick?: boolean;
  /**
   * Focus the modal itself on open instead of its first control. An embedded player would otherwise take focus, and
   * keys pressed inside a cross-origin frame never reach the modal, so Escape would not close it.
   */
  focusModalOnOpen?: boolean;
  /** The close button's accessible name; English surfaces such as Tree Studio pass "Close". */
  closeLabel?: string;
  /** Classes for the modal surface, so a surface such as Tree Studio's dark editor can restyle it. */
  className?: string;
};

const FooterContext = createContext<{ container: HTMLDivElement | null } | null>(null);
const HeaderContext = createContext<{ container: HTMLDivElement | null } | null>(null);

export function AppModalHeader({ children }: { children: React.ReactNode }) {
  const header = useContext(HeaderContext);
  if (!header) {
    return <>{children}</>;
  }
  return header.container ? createPortal(children, header.container) : null;
}

/** Keep form actions outside modal scrolling; retain inline actions on full pages. */
export function AppModalFooter({ children }: { children: React.ReactNode }) {
  const footer = useContext(FooterContext);
  if (!footer) {
    return <>{children}</>;
  }
  return footer.container ? createPortal(children, footer.container) : null;
}

export default function AppModal({
  title = "نافذة",
  description = "محتوى النافذة",
  open,
  onOpenChange,
  onAfterClose,
  children,
  desktopSize = "default",
  desktopHeight = "default",
  mobileHeight = "content",
  padding = "default",
  dismissOnOutsideClick = true,
  focusModalOnOpen = false,
  closeLabel,
  className,
}: Props) {
  // Read synchronously: a modal can mount already open, and its first render decides whether the phone-only artwork
  // below downloads at all.
  const isDesktop = useMedia("(min-width: 768px)");
  const [footerContainer, setFooterContainer] = useState<HTMLDivElement | null>(null);
  const [headerContainer, setHeaderContainer] = useState<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState<{ height: number; bottom: number } | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const scrollContainer = useRef<HTMLDivElement | null>(null);
  const headerContext = useMemo(() => ({ container: headerContainer }), [headerContainer]);
  const footerContext = useMemo(() => ({ container: footerContainer }), [footerContainer]);

  useEffect(() => {
    const visualViewport = window.visualViewport;
    if (!open || isDesktop || !visualViewport) {
      setViewport(null);
      return;
    }
    const update = () => {
      // Pin to the visible viewport when the keyboard overlays the layout viewport.
      // Leave browser pinch zoom alone so users can magnify the form.
      if (visualViewport.scale !== 1) {
        return;
      }
      setViewport({
        height: visualViewport.height,
        bottom: Math.max(0, window.innerHeight - visualViewport.height - visualViewport.offsetTop),
      });
    };
    update();
    visualViewport.addEventListener("resize", update);
    visualViewport.addEventListener("scroll", update);
    return () => {
      visualViewport.removeEventListener("resize", update);
      visualViewport.removeEventListener("scroll", update);
    };
  }, [open, isDesktop]);

  useEffect(() => {
    const scroll = scrollContainer.current;
    const field = document.activeElement;
    if (
      !viewport ||
      !scroll ||
      !(field instanceof HTMLElement) ||
      !scroll.contains(field) ||
      !field.matches("input, textarea, [contenteditable=true]")
    ) {
      return;
    }
    const bounds = scroll.getBoundingClientRect();
    const focusedBounds = field.getBoundingClientRect();
    if (focusedBounds.bottom > bounds.bottom - 16) {
      scroll.scrollTop += focusedBounds.bottom - bounds.bottom + 16;
    } else if (focusedBounds.top < bounds.top + 16) {
      scroll.scrollTop += focusedBounds.top - bounds.top - 16;
    }
  }, [viewport]);

  const desktopSizeClassName = {
    default: "md:max-w-lg",
    xl: "md:max-w-[40rem]",
    screen: "md:w-[min(96vw,1380px)] md:max-w-[min(96vw,1380px)]",
  } satisfies Record<ModalDesktopSize, string>;
  const desktopHeightClassName = {
    default: "md:max-h-[80dvh]",
    screen: "md:max-h-[calc(100dvh-2rem)]",
    "90vh": "md:max-h-[90dvh]",
  } satisfies Record<ModalDesktopHeight, string>;
  const mobileHeightClassName = {
    content: "",
    "75vh": "max-md:h-[75dvh]",
    "90vh": "max-md:h-[90dvh]",
  } satisfies Record<ModalMobileHeight, string>;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-app-modal=""
        closeLabel={closeLabel}
        onPointerDownOutside={dismissOnOutsideClick ? undefined : (event) => event.preventDefault()}
        onClickCapture={(event) => {
          // Safari does not focus buttons on pointer activation. Establish the
          // opener before a nested dialog captures its focus-return target.
          // Dropdowns can open on pointer-down: leave their focus alone once open.
          const button = event.target instanceof Element ? event.target.closest("button") : null;
          if (button && !button.disabled && button.getAttribute("aria-expanded") !== "true") {
            button.focus({ preventScroll: true });
          }
        }}
        onOpenAutoFocus={(event) => {
          returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

          if (focusModalOnOpen && event.currentTarget instanceof HTMLElement) {
            event.preventDefault();
            event.currentTarget.focus({ preventScroll: true });
          }
        }}
        onCloseAutoFocus={(event) => {
          onAfterClose?.();
          // These dialogs open through application state rather than Dialog.Trigger.
          if (returnFocus.current?.isConnected) {
            event.preventDefault();
            returnFocus.current.focus({ preventScroll: true });
          }
        }}
        className={cn(
          "bg-sidebar inset-x-0 top-auto bottom-0 flex w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-t-2xl rounded-b-none p-0 pt-12 sm:max-w-none",
          "max-h-[calc(100dvh-1rem)] md:inset-x-auto md:top-1/2 md:bottom-auto md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg md:pt-0",
          "app-modal",
          desktopSizeClassName[desktopSize],
          desktopHeightClassName[desktopHeight],
          mobileHeightClassName[mobileHeight],
          className,
        )}
        style={
          !isDesktop && viewport
            ? { bottom: viewport.bottom, maxHeight: `min(calc(100dvh - 1rem), ${Math.max(0, viewport.height - 16)}px)` }
            : undefined
        }
        closeButtonClassName="z-10 max-md:top-2 max-md:start-3 max-md:size-9 max-md:translate-x-0 max-md:translate-y-0"
        overlayClassName="app-modal-backdrop"
      >
        {/* Phone sheets only: rendering it on desktop would download an image that `md:hidden` never shows. */}
        {!isDesktop &&
          (["top", "bottom"] as const).map((edge) => (
            <div
              key={edge}
              aria-hidden="true"
              className={cn(
                "pointer-events-none absolute inset-x-0 max-h-[40%] overflow-hidden md:hidden",
                edge === "top" ? "top-0 h-16 rounded-t-2xl opacity-21" : "bottom-0 h-45 opacity-39",
              )}
              style={{ maskImage: `linear-gradient(to ${edge === "top" ? "bottom" : "top"}, black, transparent)` }}
            >
              <img
                src="/images/sheet-botanical.webp"
                alt=""
                className={cn("absolute h-auto w-full", edge === "top" ? "top-0" : "bottom-0")}
              />
            </div>
          ))}
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">{description}</DialogDescription>
        <HeaderContext.Provider value={headerContext}>
          <FooterContext.Provider value={footerContext}>
            <div
              ref={setHeaderContainer}
              data-app-modal-header=""
              className="relative shrink-0 border-b border-gray-200 px-4 pb-4 empty:hidden md:px-6 md:pt-6"
            />
            <div
              ref={scrollContainer}
              data-app-modal-scroll=""
              className={cn(
                "relative min-h-0 flex-1 scroll-py-4 overflow-y-auto overscroll-contain",
                padding === "default" ? "px-4 pt-2 pb-4 md:p-6" : "p-0",
                "max-md:pb-[max(1rem,env(safe-area-inset-bottom))]",
              )}
            >
              {children}
            </div>
            <div
              ref={setFooterContainer}
              data-app-modal-footer=""
              className="md:bg-sidebar relative shrink-0 border-t border-gray-200 p-3 empty:hidden max-md:pb-[max(0.75rem,env(safe-area-inset-bottom))] md:px-6"
            />
          </FooterContext.Provider>
        </HeaderContext.Provider>
      </DialogContent>
    </Dialog>
  );
}
